import { z } from "zod";

const locationPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

/** Partner app → staff-location: registered fallback pin. */
export const setStaffHomeLocationSchema = locationPointSchema;
export type SetStaffHomeLocationInput = z.infer<typeof setStaffHomeLocationSchema>;

/** Partner app → staff-location: one latest clocked-in GPS fix. */
export const upsertStaffLiveLocationSchema = locationPointSchema.extend({
  /** The phone's own clock time of the fix. Kept for app versions without `ageMs`. */
  capturedAt: z.string().datetime({ offset: true }),
  /**
   * 0297 — how long ago the fix was taken, measured on the phone (its clock on
   * both ends, so a wrong clock cancels out). When present the server dates the
   * fix from THIS and ignores `capturedAt`. Capped at the 10 minutes the route
   * already accepted.
   */
  ageMs: z.number().int().min(0).max(600_000).optional(),
});
export type UpsertStaffLiveLocationInput = z.infer<typeof upsertStaffLiveLocationSchema>;

/**
 * Every activity in Bam's admin-end status sheet, both roles. The engine that
 * produces them is `packages/logic` `staff-ops-status.ts`; its test asserts
 * this list and the engine's labels never drift apart.
 */
export const staffOpsActivitySchema = z.enum([
  "active",
  "pick_up_en_route",
  "drop_off_en_route",
  "return_en_route",
  "on_break",
  "waiting_for_pick_up",
  "enroute",
  "at_client_waiting_to_start",
  "on_going_service",
  "collecting_payment",
  "not_in_session",
]);
export type StaffOpsActivityValue = z.infer<typeof staffOpsActivitySchema>;

/** The computed admin-end status — activity, the Disconnected flag, and when it began. */
export const staffOpsStatusSchema = z.object({
  activity: staffOpsActivitySchema,
  connected: z.boolean(),
  /** The sheet's exact wording, "Disconnected : " prefix included. */
  label: z.string().min(1),
  since: z.string().datetime({ offset: true }).nullable(),
  lastContactAt: z.string().datetime({ offset: true }).nullable(),
  /** The booking the activity concerns — staff-side data, never a client column. */
  bookingId: z.string().uuid().nullable(),
  bookingRef: z.string().nullable(),
  pauseKind: z.enum(["regular", "emergency"]).nullable(),
  pickUpFor: z.enum(["client", "origin"]).nullable(),
});
export type StaffOpsStatusWire = z.infer<typeof staffOpsStatusSchema>;

export const staffLocationSnapshotItemSchema = z.object({
  staffId: z.string().uuid(),
  name: z.string().min(1),
  role: z.enum(["rider", "therapist"]),
  hubId: z.string().uuid(),
  point: locationPointSchema,
  /**
   * WHICH PIN is being shown — the live one or her registered home. Distinct
   * from `mapStatus` below, which says what she is doing. The admin live map
   * filters on `"live"` in several places; do not repurpose this.
   */
  state: z.enum(["live", "home"]),
  /**
   * TEND-024's six approved map statuses. `null` for riders — they have their
   * own lifecycle, and applying a therapist rule to them by assumption is
   * exactly what `never-invent-policy.md` forbids.
   *
   * ⚠ `offline` outranks active work: TEND-027 makes her Offline five minutes
   * after her last location update, and TEND-028 keeps her work running anyway.
   * A therapist mid-service with a dead phone is `offline` on the map, because
   * the pin the map holds is the last known one.
   */
  mapStatus: z
    .enum(["off_session", "active", "pause", "dispatch", "service", "offline"])
    .nullable(),
  capturedAt: z.string().datetime({ offset: true }),
  /**
   * Bam's per-role status (0270 + staff-ops-status.ts). Optional because the
   * Edge Function deploys by hand: an admin build must still render against a
   * function deployed before this field existed.
   */
  opsStatus: staffOpsStatusSchema.nullable().optional(),
});

export const staffLocationSnapshotSchema = z.array(staffLocationSnapshotItemSchema);
export type StaffLocationSnapshotItem = z.infer<typeof staffLocationSnapshotItemSchema>;

/**
 * Therapist → staff-location: request a home-anchored clock-in pin.
 * hub_manager approval is required before it takes effect (decision 9,
 * migration 0027) — the request has no effect on staff_home_locations
 * until approved. Covers both initial registration and later changes.
 */
export const submitHomeLocationRequestSchema = locationPointSchema.extend({
  radiusM: z.number().positive().max(2000),
  notes: z.string().max(500).optional(),
});
export type SubmitHomeLocationRequestInput = z.infer<typeof submitHomeLocationRequestSchema>;

/** hub_manager → staff-location: approve or reject a pending home-location
 * request, scoped to their own hub (enforced server-side from the caller's
 * verified JWT, never a client-claimed hub id). */
export const approveHomeLocationRequestSchema = z.object({
  approve: z.boolean(),
  notes: z.string().max(500).optional(),
});
export type ApproveHomeLocationRequestInput = z.infer<typeof approveHomeLocationRequestSchema>;

export const homeLocationRequestSchema = z.object({
  id: z.string().uuid(),
  staffId: z.string().uuid(),
  point: locationPointSchema,
  radiusM: z.number(),
  status: z.enum(["pending", "approved", "rejected"]),
  requestedBy: z.string().uuid(),
  reviewedBy: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime({ offset: true }).nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type HomeLocationRequest = z.infer<typeof homeLocationRequestSchema>;

/** Rider-only response. It intentionally contains no client name or address. */
export const riderNavigationDestinationSchema = z.object({
  tripId: z.string().uuid(),
  leg: z.enum(["to_pickup", "to_client", "waiting", "return"]),
  destination: locationPointSchema.optional(),
  destinationSource: z.enum(["live_location", "registered_home", "client", "hub"]).optional(),
});
export type RiderNavigationDestination = z.infer<typeof riderNavigationDestinationSchema>;

/**
 * A Therapist Return's navigation for the rider who took it (0237's
 * `rider_return_navigation`). Same contract as above: a coordinate and a leg,
 * never a name. Before Pick-Up the pin is her live position (TEND-026); after
 * it, the destination the system resolved (TEND-039). No Client appears on a
 * Return at all.
 */
export const riderReturnNavigationSchema = z.object({
  dispatchId: z.string().uuid(),
  leg: z.enum(["to_pickup", "return", "done"]),
  destination: locationPointSchema.optional(),
  destinationSource: z.enum(["live_location", "hub", "origin"]).optional(),
});
export type RiderReturnNavigation = z.infer<typeof riderReturnNavigationSchema>;

/**
 * One saved Client pin for the Super Admin map layer
 * (`Continuous Location Monitoring.md` § Super Admin map).
 *
 * ⚠ POSITION AND LABEL ONLY, AND THAT IS THE CONTRACT — not an oversight to be
 * filled in later. The spec says "saved Client pins" and stops; a name or a
 * street address on an operations map is a client directory, which is a
 * different feature nobody has approved. `ops_client_pins_snapshot` returns
 * exactly these four columns and pgTAP 148 pins the signature, so widening this
 * schema alone would fail the parse rather than leak quietly.
 */
export const clientMapPinSchema = z.object({
  pinId: z.string().uuid(),
  /** 'home' / 'office' / custom. Nullable — the column is. */
  label: z.string().nullable(),
  point: locationPointSchema,
});

export const clientMapPinsSchema = z.array(clientMapPinSchema);
export type ClientMapPin = z.infer<typeof clientMapPinSchema>;

/**
 * One client pin tied to a CURRENTLY ACTIVE booking — a different feature from
 * `clientMapPinSchema` above, not a widening of it. Leon's call, 2026-09-23:
 * superadmin may see who a booking she can already watch move on the map (the
 * therapist/rider pins, the pickup route line) actually belongs to, scoped to
 * bookings in progress right now — not the permanent saved-address layer,
 * which stays name-less by 0137's own deliberate restraint.
 */
export const activeBookingClientPinSchema = z.object({
  bookingId: z.string().uuid(),
  clientName: z.string(),
  point: locationPointSchema,
});

export const activeBookingClientPinsSchema = z.array(activeBookingClientPinSchema);
export type ActiveBookingClientPin = z.infer<typeof activeBookingClientPinSchema>;
